import { IconColor, IconSize } from '@aurora/shared-client/components/common/Icon/enums';
import Icon from '@aurora/shared-client/components/common/Icon/Icon';
import Icons from '@aurora/shared-client/icons';
import { EndUserComponent } from '@aurora/shared-types/pages/enums';
import React from 'react';
import { useClassNameMapper } from 'react-bootstrap';
import type { UserTopicsCountFragment } from '../../../types/graphql-types';
import useTranslation from '../../useTranslation';
import localStyles from './UserTopicsCount.module.css';

interface Props {
  /**
   * The user for which topics count is displayed for.
   */
  user: UserTopicsCountFragment;
  /**
   * Set a custom element for this component.
   */
  as?: React.ElementType;
  /**
   * Class name(s) to apply to the component element.
   */
  className?: string;
  /**
   * Class name(s) to apply to the icon element.
   */
  iconClassName?: string;
  /** Color to apply to the icon element */
  iconColor?: IconColor;

  /** Size to apply to the icon element */
  iconSize?: IconSize;
  /**
   * Include an icon in the component element.
   */
  useIcon?: boolean;
  /**
   * Include text in the component element.
   */
  useText?: boolean;
}

/**
 * Displays the number of topics posted by user.
 *
 * @constructor
 *
 * @author Shalin Amin
 */
const UserTopicsCount: React.FC<React.PropsWithChildren<Props>> = ({
  user,
  as: Component = 'strong',
  className,
  iconClassName,
  iconColor = IconColor.GRAY_600,
  iconSize = IconSize.PX_16,
  useIcon = false,
  useText = true
}) => {
  const cx = useClassNameMapper(localStyles);
  const { formatMessage, loading: textLoading } = useTranslation(
    EndUserComponent.USER_TOPICS_COUNT
  );

  const { topicsCount } = user;

  return !textLoading && topicsCount ? (
    <Component title={formatMessage('topicAuthored')} className={cx(className, 'lia-topics-count')}>
      {useIcon && (
        <Icon
          icon={Icons.CommentIcon}
          color={iconColor}
          size={iconSize}
          className={cx(iconClassName, 'lia-g-mr-5')}
        />
      )}
      {useText ? (
        <span> {formatMessage('topicCount', { count: topicsCount })}</span>
      ) : (
        <span>{topicsCount}</span>
      )}
    </Component>
  ) : null;
};

export default UserTopicsCount;
